import React from 'react'
import { Paper, Box, IconButton, Tooltip } from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import InfoIcon from '@mui/icons-material/Info'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import { TableViewSettingsData } from '../../types/app'
import GraphConfigControls from './GraphConfigControls'
import type { ChartType } from './types'

interface GraphViewControlsProps {
  allRows: string[];
  allColumns: string[];
  allRowTypes: string[];
  allColumnTypes: string[];
  allowedChartTypes: ChartType[];
  viewSettings: TableViewSettingsData;
  onViewSettingsChange: (settings: TableViewSettingsData) => void;
  fullscreen: boolean;
  onFullscreenToggle: () => void;
  onTableView?: () => void;
  onInfoClick?: () => void;
  onExportPptx?: () => void;
}

/**
 * GraphViewControls Component
 *
 * Controls for graph view that mirror the layout of TableControls.
 * Left side: action buttons (table view, metadata, fullscreen)
 * Right side: chart configuration controls (chart type, data type, filters)
 */
export const GraphViewControls: React.FC<GraphViewControlsProps> = ({
  allRows,
  allColumns,
  allRowTypes,
  allColumnTypes,
  allowedChartTypes,
  viewSettings,
  onViewSettingsChange,
  fullscreen,
  onFullscreenToggle,
  onTableView,
  onInfoClick,
  onExportPptx,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        pl: 1,
        pt: 0,
        borderRadius: 1,
        backgroundColor: '#ffffff',
      }}
    >
      {/* Left side: Action icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
        {!fullscreen && (
          <>
            <Tooltip title="Table View" placement="top">
              <span>
                <IconButton
                  aria-label="Table View"
                  onClick={onTableView}
                  size="small"
                  disabled={!onTableView}
                  sx={{ p: 0.5, mr: 0.5 }}
                >
                  <TableChartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Metadata" placement="top">
              <span>
                <IconButton
                  aria-label="Metadata"
                  onClick={onInfoClick}
                  size="small"
                  sx={{ p: 0.5, mr: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Export to PowerPoint" placement="top">
              <span>
                <IconButton
                  aria-label="Export to PowerPoint"
                  onClick={onExportPptx}
                  size="small"
                  disabled={!onExportPptx}
                  sx={{ p: 0.5, mr: 0.5 }}
                >
                  <SlideshowIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        <Tooltip title={fullscreen ? "Close Fullscreen" : "Enter Fullscreen"} placement="top">
          <IconButton
            aria-label={fullscreen ? "Close Fullscreen" : "Enter Fullscreen"}
            onClick={onFullscreenToggle}
            size={fullscreen ? "medium" : "small"}
            sx={{
              p: fullscreen ? 1 : 0.5,
              color: fullscreen ? 'white' : 'action.active',
              backgroundColor: fullscreen ? 'error.main' : 'transparent',
              '&:hover': {
                backgroundColor: fullscreen ? 'error.dark' : 'action.hover',
              },
            }}
          >
            {fullscreen ? <CloseIcon fontSize={fullscreen ? "medium" : "small"} /> : <OpenInFullIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Right side: Chart configuration controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
        <GraphConfigControls
          allRows={allRows}
          allColumns={allColumns}
          allRowTypes={allRowTypes}
          allColumnTypes={allColumnTypes}
          allowedChartTypes={allowedChartTypes}
          viewSettings={viewSettings}
          onViewSettingsChange={onViewSettingsChange}
        />
      </Box>
    </Paper>
  )
}

export default GraphViewControls
